import { useCallback, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { actions } from 'store';
import { ModalButtonWithForm, User } from 'components/common';
import { AdminGroupAvatar, SelectAdmin } from 'components/common/admins';
import { Checkbox, FormGroup, RadioGroup, Text } from 'components/common/form';

import { FormState } from 'informed';
import { OwnerOrAdminUser } from './OwnersAndAdminsManager';

interface Props {
  adminsOnly?: boolean;
  allowNonAdmins: boolean;
  currentOwnersAndAdmins: OwnerOrAdminUser[];
  disabled?: boolean;
  ownerTooltip?: string;
  permissions: {
    id: string;
    label: string;
    tooltip?: string;
    value: string;
  }[];
  targetOwnerOrAdmin?: OwnerOrAdminUser;
  updateOwnersAndAdmins: ({
    updatePageState
  }: {
    updatePageState: boolean;
  }) => Promise<void>;
  url: string;
}

const OwnersAndAdminsAddOrEdit = ({
  adminsOnly = false,
  allowNonAdmins,
  currentOwnersAndAdmins,
  disabled = false,
  ownerTooltip,
  permissions,
  targetOwnerOrAdmin,
  updateOwnersAndAdmins,
  url
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    serverConfig: { enableUserSearch },
    user
  } = useReduxState();

  const [hasSelectedPermission, setHasSelectedPermission] = useState(true);

  const requiresPermission = adminsOnly && permissions.length > 0;
  const permissionError =
    requiresPermission && !hasSelectedPermission
      ? messages.settingsAddAdminPermissionsError
      : undefined;

  const submit = async (values: {
    userId: { adminGroupId?: number; userId?: number };
    userEmail: string;
    permissions: { [key: string]: boolean };
    role: string;
  }) => {
    // Admins-only lists have no owners, so they never render the role field.
    const role = adminsOnly ? 'admin' : values.role || 'owner';

    const packet = {
      role,
      userEmail: values.userEmail || null,
      ...(values.userId ? { ...values.userId } : {}),
      ...(values.permissions
        ? {
            permissions: Object.keys(values.permissions).filter(
              (permission) => values.permissions[permission]
            )
          }
        : {})
    };
    if (targetOwnerOrAdmin) {
      if (targetOwnerOrAdmin.isGroup) {
        packet.adminGroupId = targetOwnerOrAdmin.id;
      } else packet.userId = targetOwnerOrAdmin.id;
    }

    const response = await api.post(url, packet);

    let error: string;
    if (response.data && response.data.error === 'user_not_found') {
      error = messages.settingsAddAdminUserNotFound;
    } else if (response.data?.error === 'group_owner_must_be_admin') {
      error = messages.groupsAdminsOwnerAdminOnly;
    } else if (response.data?.error === 'group_admin_must_be_admin') {
      error = messages.groupsAdminsAdminOnly;
    } else if (!response.data || !response.data.success) {
      throw new Error('error');
    }

    if (error) {
      await dispatch.handleErrorDispatch(null, error);
      return false;
    }

    await updateOwnersAndAdmins({ updatePageState: true });
    dispatch(
      actions.addNotice({
        text:
          role === 'owner'
            ? messages.commonOwnerAddSuccess
            : targetOwnerOrAdmin
            ? messages.settingsEditAdminSuccess
            : messages.settingsAddAdminSuccess,
        type: 'success'
      })
    );

    return true;
  };

  const validatePermissions = useCallback(
    (value, { permissions, role }) => {
      const valid =
        role !== 'admin' ||
        (permissions &&
          Object.keys(permissions).find((key) => permissions[key]));
      return valid ? undefined : messages.settingsAddAdminPermissionsError;
    },
    [messages]
  );

  const validateUser = (value: string) => {
    return value ? undefined : messages.commonFieldRequired;
  };

  const handleValueChange = (values: {
    permissions?: { [key: string]: boolean };
  }) => {
    if (!requiresPermission) return;

    setHasSelectedPermission(
      Object.values(values.permissions || {}).some((isSelected) => isSelected)
    );
  };

  const buttonTextAdd = adminsOnly
    ? messages.settingsAddAdmin
    : permissions.length > 1
    ? messages.settingsAddAdmin
    : messages.commonOwnerAdd;

  const getInitialPermissions = () => {
    if (targetOwnerOrAdmin?.permissions) {
      return permissions.reduce((values, permission) => {
        if (targetOwnerOrAdmin.permissions.includes(permission.value)) {
          values[permission.value] = true;
        }
        return values;
      }, {});
    }

    // A new admin on an admins-only list starts with every permission ticked.
    if (adminsOnly) {
      return permissions.reduce((values, permission) => {
        values[permission.value] = true;
        return values;
      }, {});
    }

    return null;
  };

  const initialPermissions = getInitialPermissions();

  const initialValues = {
    role: targetOwnerOrAdmin?.permissions ? 'admin' : 'owner',
    ...(initialPermissions ? { permissions: initialPermissions } : {})
  };

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonDisabled={disabled}
      buttonIcon={targetOwnerOrAdmin ? 'pen' : 'plus'}
      buttonIsDropdown={Boolean(targetOwnerOrAdmin)}
      buttonSize="header"
      buttonText={targetOwnerOrAdmin ? messages.commonEdit : buttonTextAdd}
      formOnValueChange={handleValueChange}
      initialValues={initialValues}
      modalActionText={
        targetOwnerOrAdmin ? messages.commonSave : messages.commonAdd
      }
      modalTitle={targetOwnerOrAdmin ? messages.commonEdit : buttonTextAdd}
      onShow={() => setHasSelectedPermission(true)}
      onSubmit={submit}
      submitDisabled={Boolean(permissionError)}
    >
      {(formState: FormState) => (
        <>
          {targetOwnerOrAdmin ? (
            targetOwnerOrAdmin.isGroup ? (
              <AdminGroupAvatar
                adminGroup={targetOwnerOrAdmin}
                hideLock={true}
              />
            ) : (
              <User enableProfileLink={true} user={targetOwnerOrAdmin} />
            )
          ) : (
            <FormGroup label={messages.settingsUserEmail}>
              {enableUserSearch || user?.roles.includes('superadmin') ? (
                <SelectAdmin
                  error={formState.errors.userId}
                  existingAdminGroupIds={currentOwnersAndAdmins
                    .filter((admin) => admin.isGroup)
                    .map((admin) => admin.id)}
                  existingUserIds={currentOwnersAndAdmins
                    .filter((admin) => !admin.isGroup)
                    .map((admin) => admin.id)}
                  includeAdminGroups={true}
                  isRequired={true}
                  searchParams={allowNonAdmins ? '' : '&role=admin'}
                />
              ) : (
                <Text
                  error={formState.errors.userEmail}
                  field="userEmail"
                  id="admin-email"
                  placeholder={messages.commonEmailAddress}
                  validate={validateUser}
                  validateOnChange={true}
                />
              )}
            </FormGroup>
          )}

          {permissions.length ? (
            <FormGroup
              error={permissionError}
              label={
                adminsOnly ? messages.usersPermissions : messages.settingsRole
              }
            >
              {adminsOnly ? null : (
                <RadioGroup
                  error={formState.errors.role}
                  field="role"
                  options={[
                    {
                      id: 'admins-add-owner',
                      label: messages.commonOwner,
                      tooltip: ownerTooltip,
                      value: 'owner'
                    },
                    {
                      id: 'admins-add-admin',
                      label: messages.settingsLimitedPermissions,
                      value: 'admin'
                    }
                  ]}
                  validate={validatePermissions}
                />
              )}
              <div style={adminsOnly ? undefined : { paddingLeft: 25 }}>
                {permissions.map((permission) => (
                  <div
                    key={`admins-add-${permission.id}`}
                    style={{ marginTop: 10 }}
                  >
                    <Checkbox
                      disabled={
                        !adminsOnly && formState.values.role !== 'admin'
                      }
                      field={`permissions['${permission.value}']`}
                      id={`admins-add-${permission.id}`}
                      label={permission.label}
                      labelTooltip={permission.tooltip}
                    />
                  </div>
                ))}
              </div>
            </FormGroup>
          ) : null}
        </>
      )}
    </ModalButtonWithForm>
  );
};

export default OwnersAndAdminsAddOrEdit;
